import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { X, Megaphone, AlertTriangle, CheckCircle2, Pin, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";

const iconFor = (v) => (v === "warning" ? AlertTriangle : v === "success" ? CheckCircle2 : Megaphone);
const styleFor = (v) =>
  v === "warning"
    ? "bg-amber-50 border-amber-300 text-amber-900"
    : v === "success"
    ? "bg-emerald-50 border-emerald-300 text-emerald-900"
    : "bg-sky-50 border-sky-300 text-sky-900";

// Session-scoped dismissal so a non-pinned banner stops showing until the
// next tab reload. Pinned announcements ignore this and use server-side
// dismissed_by so they only vanish permanently after the user clicks Dismiss.
const SESSION_KEY = "summit_air_dismissed_announcements";
const getSession = () => {
  try { return JSON.parse(sessionStorage.getItem(SESSION_KEY) || "[]"); } catch { return []; }
};
const setSession = (arr) => sessionStorage.setItem(SESSION_KEY, JSON.stringify(arr));

export const AnnouncementsBanner = () => {
  const [items, setItems] = useState([]);
  const [sessionDismissed, setSessionDismissed] = useState(getSession());
  const [pinnedModalIds, setPinnedModalIds] = useState([]);

  useEffect(() => {
    api.get("/announcements/active").then((r) => {
      setItems(r.data || []);
      // Pinned + not yet dismissed server-side → surface as modal on this load.
      const pinnedIds = (r.data || [])
        .filter((a) => a.pinned && !a.dismissed_by_me)
        .map((a) => a.id);
      setPinnedModalIds(pinnedIds);
    }).catch(() => {});
  }, []);

  const dismissSession = (id) => {
    const next = [...sessionDismissed, id];
    setSessionDismissed(next);
    setSession(next);
  };

  const dismissPersistent = async (id) => {
    // Optimistic: hide immediately, then persist.
    setItems((arr) => arr.map((a) => (a.id === id ? { ...a, dismissed_by_me: true } : a)));
    setPinnedModalIds((ids) => ids.filter((x) => x !== id));
    try { await api.post(`/announcements/${id}/dismiss`); } catch { /* keep client-side dismiss */ }
  };

  // Non-modal, non-pinned banners = show inline until session-dismissed.
  const inlineVisible = items.filter((a) => {
    if (a.pinned) return false; // pinned handled by modal
    if (a.dismissed_by_me) return false;
    if (sessionDismissed.includes(a.id)) return false;
    return true;
  });

  const modalItem = pinnedModalIds.length > 0
    ? items.find((a) => a.id === pinnedModalIds[0])
    : null;

  return (
    <>
      {/* --- Inline banner strip (non-pinned) --- */}
      {inlineVisible.length > 0 && (
        <div className="border-b border-summit-hairline bg-white" data-testid="announcements-banner">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 space-y-2">
            {inlineVisible.map((a) => {
              const Icon = iconFor(a.variant);
              return (
                <div
                  key={a.id}
                  className={`flex items-start gap-3 border rounded-md px-3 py-2 ${styleFor(a.variant)}`}
                  data-testid={`announcement-${a.id}`}
                >
                  <Icon className="w-4 h-4 mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium">{a.title}</div>
                    <div className="text-xs opacity-90 mt-0.5 whitespace-pre-wrap">{a.body}</div>
                    <div className="font-mono text-[10px] opacity-70 mt-1">
                      — {a.author_name} · {a.author_job_title}
                      {a.audience && a.audience !== "all" && (
                        <span className="ml-1.5 px-1 rounded bg-white/60 uppercase tracking-widest">
                          {a.audience === "leadership" ? "Leadership" : a.target_department || "Dept"}
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => dismissSession(a.id)}
                    className="p-1 rounded hover:bg-white/50"
                    aria-label="Dismiss"
                    data-testid={`announcement-dismiss-${a.id}`}
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* --- Pinned modal (blocks until dismissed) --- */}
      {modalItem && (
        <div
          className="fixed inset-0 z-[100] bg-black/50 flex items-center justify-center p-4 animate-in fade-in"
          data-testid={`announcement-modal-${modalItem.id}`}
        >
          <div className="bg-white rounded-lg border border-summit-hairline max-w-lg w-full shadow-2xl overflow-hidden">
            <div className={`px-5 py-3 border-b border-summit-hairline flex items-center gap-2 ${styleFor(modalItem.variant)}`}>
              <Pin className="w-4 h-4" />
              <div className="font-mono text-[10px] uppercase tracking-widest">
                Pinned announcement
              </div>
            </div>
            <div className="px-5 py-5">
              <div className="text-lg font-medium tracking-tight" data-testid="announcement-modal-title">
                {modalItem.title}
              </div>
              <div className="mt-2 text-sm text-gray-700 whitespace-pre-wrap" data-testid="announcement-modal-body">
                {modalItem.body}
              </div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mt-4">
                — {modalItem.author_name} · {modalItem.author_job_title}
              </div>
            </div>
            <div className="px-5 py-3 border-t border-summit-hairline bg-summit-canvas flex items-center justify-between">
              <Link
                to="/announcements"
                className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest text-summit-primary hover:underline"
                data-testid="announcement-modal-view-all"
                onClick={() => dismissPersistent(modalItem.id)}
              >
                View all <ExternalLink className="w-3 h-3" />
              </Link>
              <button
                onClick={() => dismissPersistent(modalItem.id)}
                className="px-4 py-1.5 rounded bg-summit-primary hover:bg-summit-primary-hover text-white text-sm"
                data-testid="announcement-modal-dismiss"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
